// Inter-library transit wire contracts — the transfer log that
// db/migrations/0007_serials_transit.sql backs (lib_transit). Browser-safe zod
// only.
//
// Contract law (pinned):
//   · Transit re-keys to the lib_copy SURROGATE (copyId) — the source keyed on
//     the mutable copies.barcode (L0 law). The Transit DTO is the source
//     TransitSchema PLUS copyId (the L3 Loan.copyId re-key precedent); the
//     barcode, book title, branch NAMES and patron NAME are all live joins (the
//     spec's transit identity law: opaque id + JOIN-for-display, store NO
//     denormalised display name — unlike reading_session.patronName).
//   · CreateTransit still takes copyBarcode (the desk scans a barcode; the
//     service resolves it to copy_id — the L3 IssueBody.barcodes precedent).
//     Missing copy / branch are body-ref BAD_REQUEST (no LIB code — the L2/L3
//     body-ref law).
//   · status / direction are CLOSED enums, non-null on read: the vocabularies
//     are fully known (the write enums) and the columns are NOT NULL (status) /
//     NOT NULL DEFAULT (direction) — tighter than the source's open/nullable
//     read, justified by the L5 accent + L3 Loan-status precedents.
//   · The added staff actors (shipped_by/received_by) are storage-only — kept
//     OFF this DTO to preserve the source TransitSchema shape (the issued_by
//     precedent); accountability lives in the column, not the wire.
//   · REBUILD MANDATE (api lane, out of scope here): enforce ordered transitions
//     (source accepted ANY status — code-free platform CONFLICT, the L4
//     mandated-guard precedent) and repoint lib_copy.branch_id = to_branch_id on
//     receive (the source NEVER moved the copy's home branch).
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";

/* ────────────────────────────── vocabularies ────────────────────────────── */

/**
 * The six transit statuses (source UpdateTransitSchema enum). The MANDATED
 * ordering the api lane enforces is Requested → In Transit →
 * {Received|Returned|Completed}; 'Overdue' is a flag with no side-effect (an
 * In-Transit item past its expected return). The source accepted ANY of these
 * on update with no guard — the transition table + its enforcement are api-lane
 * logic (this schema, like the source, carries no `from` to validate against).
 */
export const TRANSIT_STATUSES = ["Requested", "In Transit", "Received", "Returned", "Overdue", "Completed"] as const;
export const TransitStatus = z.enum(TRANSIT_STATUSES);
export type TransitStatus = z.infer<typeof TransitStatus>;

/**
 * The statuses that CLOSE a transit — the service stamps received_at AND flips
 * the copy back to 'Available' (source circulation.service.ts:299-302). On
 * rebuild the receive path must ALSO repoint lib_copy.branch_id = to_branch_id.
 */
export const TRANSIT_TERMINAL_STATUSES = ["Received", "Returned", "Completed"] as const satisfies readonly TransitStatus[];

/** Source CreateTransit.direction enum. */
export const TRANSIT_DIRECTIONS = ["Outgoing", "Incoming"] as const;
export const TransitDirection = z.enum(TRANSIT_DIRECTIONS);
export type TransitDirection = z.infer<typeof TransitDirection>;

/* ─────────────────────────────────── transit ─────────────────────────────────── */

/**
 * GET /circulation/transit row (source TransitSchema + the surrogate copyId).
 * copyBarcode/bookTitle/fromBranch/toBranch/patronName are LIVE joins (derived
 * per request — the transit identity law). fromBranchId/toBranchId are the
 * stored endpoint ids; patronId ← lib_transit.patron_id (the requesting patron,
 * nullable — there is NO staff/actor id on the wire).
 */
export const Transit = z.object({
  id: EntityId, // source 'tr-…' ids port verbatim; new rows mint ULIDs
  copyId: EntityId, // NEW — the surrogate FK; the barcode is join-resolved
  copyBarcode: z.string().nullable(), // live join (lib_copy.barcode)
  bookTitle: z.string().nullable(), // live join (lib_book.title)
  fromBranchId: EntityId,
  fromBranch: z.string().nullable(), // live join (lib_branch.name)
  toBranchId: EntityId,
  toBranch: z.string().nullable(), // live join (lib_branch.name)
  patronId: EntityId.nullable(), // ← patron_id (requesting patron; nullable)
  patronName: z.string().nullable(), // live join (lib_member.display_name)
  direction: TransitDirection, // non-null (NOT NULL DEFAULT 'Outgoing')
  status: TransitStatus,
  shippedAt: EpochMs.nullable(), // stamped when status → 'In Transit'
  receivedAt: EpochMs.nullable(), // stamped when status → terminal
  expectedReturn: EpochMs.nullable(),
  notes: z.string().nullable(),
});
export type Transit = z.infer<typeof Transit>;

/**
 * POST /circulation/transit — "Request a transfer". copyBarcode is the desk
 * scan (resolved to copy_id server-side); both branch ids are required. The
 * service atomically inserts the log ('Requested', shipped_at null) and flips
 * the copy to 'In Transit' at REQUEST time (source wart, carried) — there is NO
 * copy-status guard, so a Checked Out / Reserved copy can be requested.
 */
export const CreateTransit = z.object({
  copyBarcode: z.string().trim().min(1),
  fromBranchId: EntityId,
  toBranchId: EntityId,
  patronId: EntityId.optional(),
  direction: TransitDirection.optional(), // service default 'Outgoing'
  expectedReturn: EpochMs.optional(),
  notes: z.string().trim().max(1000).optional(),
});
export type CreateTransit = z.infer<typeof CreateTransit>;

/**
 * PATCH /circulation/transit/:id — "dispatch / receive" (source
 * UpdateTransitSchema: status required, notes optional). status → 'In Transit'
 * stamps shipped_at; a terminal status stamps received_at and frees the copy.
 * The schema accepts ANY status; the ordered-transition guard is api-lane logic
 * (a bad transition answers platform CONFLICT — the L4 mandated-guard law).
 */
export const UpdateTransit = z.object({
  status: TransitStatus,
  notes: z.string().trim().max(1000).optional(),
});
export type UpdateTransit = z.infer<typeof UpdateTransit>;

/**
 * Keyset list on (shipped_at DESC, id) — source order (unshipped Requested rows
 * sort last). ?status / ?direction are closed-enum exact-eq filters (source
 * TransitQuery took open strings; the vocab is fully known — the closed enums
 * ride the (status,…)/(direction,…) keyset composites).
 */
export const TransitListQuery = CursorQuery.extend({
  status: TransitStatus.optional(),
  direction: TransitDirection.optional(),
});
export type TransitListQuery = z.infer<typeof TransitListQuery>;
